import { BOARD_SIZE } from '../game/board';

/**
 * 断面（切り出し）操作の純粋な座標対応ロジック。
 * 3D 表示（render）と 2D グリッド（ui）の両方が同じ座標計算を使うことで、
 * 「2D グリッドのマス = 3D 盤面の正しい位置」を維持する。
 *
 * 重要: ここは表示の座標変換のみで、ゲームロジック（src/game/）には一切触れない。
 * 切り出しによる非表示は表示上の状態であり、石の削除や確率抽選には影響しない。
 */

type Axis = 'x' | 'y' | 'z';

/** 盤面の 6 面（内部座標系で識別。カメラの向きに依存しない） */
export type Face = 'x-' | 'x+' | 'y-' | 'y+' | 'z-' | 'z+';

export const FACES: readonly Face[] = ['x-', 'x+', 'y-', 'y+', 'z-', 'z+'];

const MAX = BOARD_SIZE - 1;

export interface FaceInfo {
  face: Face;
  label: string;
  /** 断面で固定される座標軸 */
  fixed: Axis;
  /** 盤面外側を向く法線（視点を断面へ移動する際に使う） */
  outward: [number, number, number];
}

export const FACE_INFO: Record<Face, FaceInfo> = {
  'x-': { face: 'x-', label: 'X−面', fixed: 'x', outward: [-1, 0, 0] },
  'x+': { face: 'x+', label: 'X＋面', fixed: 'x', outward: [1, 0, 0] },
  'y-': { face: 'y-', label: 'Y−面', fixed: 'y', outward: [0, -1, 0] },
  'y+': { face: 'y+', label: 'Y＋面', fixed: 'y', outward: [0, 1, 0] },
  'z-': { face: 'z-', label: 'Z−面', fixed: 'z', outward: [0, 0, -1] },
  'z+': { face: 'z+', label: 'Z＋面', fixed: 'z', outward: [0, 0, 1] },
};

function normalComponent(face: Face): number {
  const info = FACE_INFO[face];
  const i = info.fixed === 'x' ? 0 : info.fixed === 'y' ? 1 : 2;
  return info.outward[i];
}

/** 面＋深さ d における断面の固定座標値（例: X＋面・深さ2 → x = 7） */
export function sectionFixedValue(face: Face, depth: number): number {
  return normalComponent(face) > 0 ? MAX - depth : depth;
}

/**
 * 断面面が「深さが増える方向」へ進む単位ベクトル（外向き法線と逆）。
 * 例: X−面（外向き = −x）では深さ増加方向は +x（盤面内側）。
 */
export function sectionInwardVector(face: Face): [number, number, number] {
  const [ox, oy, oz] = FACE_INFO[face].outward;
  return [-ox, -oy, -oz];
}

/**
 * 深さ `startDepth` の断面面を 3D 座標のオフセット `offset` で動かした
 * ときの深さ（連続値。0..9 にクランプし、整数丸めはしない）。
 * `offset` は 3D シーンと同じ単位（1 = 1 層、盤面中心を原点）。
 * 固定軸方向（法線方向）の成分だけが深さに効く。
 */
export function sectionDepthFromOffset(
  face: Face,
  startDepth: number,
  offset: [number, number, number],
): number {
  const [ix, iy, iz] = sectionInwardVector(face);
  const delta = ix * offset[0] + iy * offset[1] + iz * offset[2];
  return Math.min(MAX, Math.max(0, startDepth + delta));
}

/** 深さを 0..9 の整数に丸める（ドラッグ中のスナップ用） */
export function snapDepth(depth: number): number {
  return Math.min(MAX, Math.max(0, Math.round(depth)));
}

/**
 * 切り出しで非表示にする領域（断面より「手前側」の層。断面層自体は含まない）にあるか。
 * 例: X−面・深さ5 → x = 0..4 が非表示、x = 5 が断面、x = 6..9 は表示。
 */
export function isCutawayCell(
  face: Face,
  depth: number,
  x: number,
  y: number,
  z: number,
): boolean {
  const info = FACE_INFO[face];
  const v = sectionFixedValue(face, depth);
  const c = info.fixed === 'x' ? x : info.fixed === 'y' ? y : z;
  return normalComponent(face) < 0 ? c < v : c > v;
}

interface AxisSpec {
  axis: Axis;
  /** 座標の増加方向が右から左（または上から下）に反転するか */
  inverted: boolean;
}

/**
 * 各面の 2D グリッドの向き。
 * 規約: 選択した面を外側から見たときと同じ視点（3D カメラの自動調整と一致）。
 * row: 0 = 上、col: 0 = 左。
 *
 * 例: X−面（x 断面）は y を縦軸（上から y=9 へ下がる）、z を横軸（左から z=0 へ）にする。
 */
const GRID_AXES: Record<Face, { row: AxisSpec; col: AxisSpec }> = {
  'x-': { row: { axis: 'y', inverted: true }, col: { axis: 'z', inverted: false } },
  'x+': { row: { axis: 'y', inverted: true }, col: { axis: 'z', inverted: true } },
  'y-': { row: { axis: 'z', inverted: true }, col: { axis: 'x', inverted: false } },
  'y+': { row: { axis: 'z', inverted: true }, col: { axis: 'x', inverted: true } },
  'z-': { row: { axis: 'y', inverted: true }, col: { axis: 'x', inverted: true } },
  'z+': { row: { axis: 'y', inverted: true }, col: { axis: 'x', inverted: false } },
};

export interface SectionCell {
  row: number;
  col: number;
  x: number;
  y: number;
  z: number;
}

/** グリッドの (row, col) → 3D 座標（断面面上のマス） */
export function gridToCell(face: Face, depth: number, row: number, col: number): SectionCell {
  const { row: rs, col: cs } = GRID_AXES[face];
  const v = sectionFixedValue(face, depth);
  const r = rs.inverted ? MAX - row : row;
  const c = cs.inverted ? MAX - col : col;
  const coords: Record<Axis, number> = { x: 0, y: 0, z: 0 };
  coords[rs.axis] = r;
  coords[cs.axis] = c;
  coords[FACE_INFO[face].fixed] = v;
  return { row, col, x: coords.x, y: coords.y, z: coords.z };
}

/** 3D 座標 → グリッドの (row, col)（座標は断面面上でなければならない） */
export function cellToGrid(
  face: Face,
  x: number,
  y: number,
  z: number,
): { row: number; col: number } {
  const { row: rs, col: cs } = GRID_AXES[face];
  const get = (a: Axis): number => (a === 'x' ? x : a === 'y' ? y : z);
  const r = get(rs.axis);
  const c = get(cs.axis);
  return {
    row: rs.inverted ? MAX - r : r,
    col: cs.inverted ? MAX - c : c,
  };
}

/** グリッドの行・列が対応する座標軸（ラベル表示用） */
export function sectionAxes(face: Face): { rowAxis: Axis; colAxis: Axis } {
  const { row, col } = GRID_AXES[face];
  return { rowAxis: row.axis, colAxis: col.axis };
}

/** 盤面中心からのオフセット（0..9 を -4.5..4.5 に変換） */
const HALF = (BOARD_SIZE - 1) / 2;

export interface SectionCameraPose {
  /** カメラの位置（盤面中央を原点とする 3D 座標） */
  position: [number, number, number];
  /** カメラが向く点（断面の中心） */
  target: [number, number, number];
}

/**
 * 「選択面の外側から断面を見ている」カメラの姿勢（位置＋見据える点）を計算する。
 * 純粋関数なので、実際のカメラ操作（render/scene.ts）と独立にテストできる。
 *
 * - target: 断面の中心。固定軸が `v - HALF`、残り 2 軸が 0。
 * - position: 断面中心から外向き法線方向 11 ユニット進み、
 *   法線に直交する 2 軸に 3.2 のオフセット（完全に正面になりすぎない 3/4 視点）。
 */
export function sectionCameraPose(face: Face, depth: number): SectionCameraPose {
  const info = FACE_INFO[face];
  const v = sectionFixedValue(face, depth);
  const fixedIndex = info.fixed === 'x' ? 0 : info.fixed === 'y' ? 1 : 2;

  const target: [number, number, number] = [0, 0, 0];
  target[fixedIndex] = v - HALF;

  const position: [number, number, number] = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const offset = i === fixedIndex ? 0 : 3.2;
    position[i] = target[i] + info.outward[i] * 11 + offset;
  }

  return { position, target };
}
